import type { ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { Select, MultiValue, OptionType } from "ui/select";
import { TextFieldWithCounter } from "ui/text-field";
import { Switch } from "ui/switch";

interface CreativeFormFieldsProps {
  populationTypeOptions: OptionType[];
  contentTypeOptions: OptionType[];
  classTypeOptions: OptionType[];
  fyOptions?: OptionType[];
  rowStartSlot?: ReactNode;
  isPopulationTypeLocked?: boolean;
  isContentTypeLocked?: boolean;
  copyCodeSlot?: ReactNode;
  afterClassSlot?: ReactNode;
  resetKey?: string;
  children?: ReactNode;
}

const CreativeFormFields = ({
  populationTypeOptions,
  contentTypeOptions,
  classTypeOptions,
  fyOptions,
  rowStartSlot,
  isPopulationTypeLocked,
  isContentTypeLocked,
  copyCodeSlot,
  afterClassSlot,
  resetKey,
  children,
}: CreativeFormFieldsProps) => {
  const {
    control,
    formState: { isSubmitted },
  } = useFormContext();

  return (
    <>
      <div className="flex gap-2">
        <div className="flex-1 min-w-0">
          <Controller
            name="populationType"
            control={control}
            render={({ field: { value, onChange, ...field }, fieldState }) => (
              <Select
                {...field}
                key={`population-type-${resetKey ?? ""}`}
                label="Population type"
                selectName="population-type-select"
                ariaLabel="Select population type"
                required
                disabled={isPopulationTypeLocked}
                options={populationTypeOptions}
                value={
                  populationTypeOptions.find((option) => option.value === value) ??
                  (isPopulationTypeLocked ? populationTypeOptions[0] : null)
                }
                onChange={(option) => onChange(option?.value ?? "")}
                errMessage={
                  fieldState.isTouched || isSubmitted ? fieldState.error?.message : undefined
                }
              />
            )}
          />
        </div>
        <div className="flex-1 min-w-0">
          <Controller
            name="contentType"
            control={control}
            render={({ field: { value, onChange, ...field }, fieldState }) => (
              <Select
                {...field}
                key={`content-type-${resetKey ?? ""}`}
                label="Content type"
                selectName="content-type-select"
                ariaLabel="Select content type"
                required
                disabled={isContentTypeLocked}
                options={contentTypeOptions}
                value={contentTypeOptions.find((option) => option.value === value) ?? null}
                onChange={(option) => onChange(option?.value ?? "")}
                errMessage={
                  fieldState.isTouched || isSubmitted ? fieldState.error?.message : undefined
                }
              />
            )}
          />
        </div>
      </div>
      <div className="flex gap-2">
        {!!rowStartSlot && <div className="flex-1 min-w-0">{rowStartSlot}</div>}
        {!!fyOptions && (
          <div className="flex-1 min-w-0">
            <Controller
              name="fiscalYear"
              control={control}
              render={({ field: { value, onChange, ...field }, fieldState }) => (
                <Select
                  {...field}
                  label="Fiscal year"
                  selectName="fiscal-year-select"
                  ariaLabel="Select fiscal year"
                  required
                  options={fyOptions}
                  value={fyOptions.find((option) => option.value === value) ?? null}
                  onChange={(selected) => {
                    onChange(selected ? Number(selected.value) : null);
                  }}
                  errMessage={fieldState.error?.message}
                />
              )}
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {copyCodeSlot ?? (
            <Controller
              name="copyCode"
              control={control}
              render={({ field, fieldState }) => (
                <TextFieldWithCounter
                  label="Copy code"
                  required
                  errMessage={fieldState.error?.message}
                  {...field}
                />
              )}
            />
          )}
        </div>
      </div>
      <Controller
        name="classes"
        control={control}
        render={({ field: { value, onChange, ...field }, fieldState }) => {
          const selectedOptions = (Array.isArray(value) ? value : [])
            .map((classType: string) =>
              classTypeOptions.find((option) => option.value === classType),
            )
            .filter((option): option is OptionType => option !== undefined);

          return (
            <Select
              {...field}
              label="Class"
              selectName="class-select"
              ariaLabel="Select classes"
              required
              multiple
              options={classTypeOptions}
              value={selectedOptions}
              onChange={(selected) => onChange(selected.map((option) => String(option.value)))}
              errMessage={
                fieldState.isTouched || isSubmitted ? fieldState.error?.message : undefined
              }
            />
          );
        }}
      />
      {afterClassSlot}
      {/* Toggles sit closer together than the rest of the form fields to save vertical space. */}
      <div className="flex flex-col gap-2.5">
        <Controller
          name="insightsActivator"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Switch
              toggleName="Insights Activator powered"
              checked={!!value}
              onChange={onChange}
            />
          )}
        />
        {children}
      </div>
    </>
  );
};

export default CreativeFormFields;
